import {
  DataGrid,
  DataGridProps,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridValidRowModel,
  MuiEvent,
  useGridApiRef
} from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { getSortString, notif } from "../utils/util";

export type PaginationData<T extends GridValidRowModel = any> = {
  data: T[];
  count: number;
};

export type PaginatedDataGridProps<T extends GridValidRowModel = any> = {
  name?: string;
  columns: GridColDef<T>[];
  loadData: (page: number, size: number, sort: string, filter: string) => Promise<PaginationData<T>>;
  initPage?: number;
  initPageSize?: number;
  getRowId?: (row: T) => GridRowId;
  onRowSelected?: (row: T | null) => void;
  onRowsSelected?: (rows: T[]) => void;
  onRowDoubleClick?: (row: T | null) => void;
  rowHeight?: number;
  showFilter?: boolean;
  checkboxSelection?: boolean;
  reload?: any;
} & Omit<DataGridProps<T>, "rows" | "columns" | "onRowClick" | "onRowDoubleClick" | "rowHeight" | "checkboxSelection">;

type LastCellState = {
  id: GridRowId;
  field: string;
  handled: boolean;
}

export const PaginatedDataGrid = <T extends GridValidRowModel = any>(props: PaginatedDataGridProps<T>) => {
  const { name, columns, loadData, initPage, initPageSize, getRowId, onRowSelected, onRowsSelected, onRowDoubleClick, rowHeight, showFilter, reload, ...rest } = props;

  const apiRef = useGridApiRef();
  const [data, setData] = useState<PaginationData<T>>({ data: [], count: 0 });
  const [sort, setSort] = useState<GridSortModel>([]);
  const [filter, setFilter] = useState("");
  const [lastCell, setLastCell] = useState<LastCellState>({ id: "", field: "", handled: false });
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: initPage ?? 0, pageSize: initPageSize ?? 10 });

  const log = (message?: any, ...optionalParams: any[]): void => {
    console.log(`[${name}] ${message}`, ...optionalParams);
  }

  const getSelectedRows = (): T[] => {
    const map = apiRef.current.getSelectedRows();
    return Array.from(map, ([key, value]) => value as T);
  };

  const notifySelectionChanged = () => {
    const rows = getSelectedRows();
    if (onRowSelected) onRowSelected(rows.length === 1 ? rows[0] : null);
    if (onRowsSelected) onRowsSelected(rows);
  }

  const handleGetRowId = <T extends GridValidRowModel = any>(row: T | null): GridRowId => {
    if (getRowId) return getRowId(row as any);
    return row?.id ?? "";
  };

  const loadPaginationData = useCallback(async () => {
    try {
      if (loadData) {
        const result = await loadData(pagination.page + 1, pagination.pageSize, getSortString(sort), filter);
        setData(result);
      }
    } catch (err) {
      setData({ data: [], count: 0 } as any);
      notif(err);
    }
    setIsLoading(false);
  }, [pagination, sort, filter, loadData]);

  const handlePaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails) => {
    setPagination(model);
  };

  const handlesortModelChange = (model: GridSortModel, details: GridCallbackDetails) => {
    setSort(model);
  };

  const handleColumnHeaderClick = (e: any/*GridColumnHeaderParams<GridValidRowModel, any, any>*/) => {
    // console.log(`header cell click: ${JSON.stringify({ id: e.id, field: e.field })}`);
    lastCell.id = e.id;
    lastCell.field = e.field;
    lastCell.handled = false;
  }

  const handleCellClick = (e: any /*GridCellParams<T, unknown, unknown, GridTreeNode>*/) => {
    // console.log(`cell click: ${JSON.stringify({ id: e.id, field: e.field })}`);
    lastCell.id = e.id;
    lastCell.field = e.field;
    lastCell.handled = false;
  }

  const handleRowSelectionModelChange = (model: GridRowSelectionModel, details: GridCallbackDetails) => {
    if (lastCell.handled) return;
    lastCell.handled = true;
    if (lastCell.field !== "__check__" && lastCell.id) {
      const isSelected = apiRef.current.isRowSelected(lastCell.id)
      if (isSelected) {
        apiRef.current.setRowSelectionModel([lastCell.id]);
      }      
    }    
    notifySelectionChanged();
    lastCell.handled = false;
  };

  const handleRowDoubleClick = (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>, details: GridCallbackDetails) => {
    if (onRowDoubleClick) onRowDoubleClick(params.row as T);
  }

  useEffect(() => {
    loadPaginationData();
  }, [pagination, sort, columns, loadPaginationData, reload]);

  // useEffect(() => {
  // }, [data]);

  const customToolbar = () => {
    return (
      <GridToolbarContainer>
        {(showFilter ?? false) && (
          <GridToolbarQuickFilter 
            size="small" 
            quickFilterParser={(value) => { setFilter(value); return []; }} 
            quickFilterFormatter={() => filter} />
        )}
      </GridToolbarContainer>
    );
  }

  return (
    <DataGrid
      apiRef={apiRef}
      slots={{ toolbar: customToolbar }}
      rows={data?.data ?? []}
      rowCount={data?.count ?? 0}
      loading={isLoading}
      autoHeight
      rowHeight={rowHeight || 30}
      pagination
      paginationMode="server"
      onPaginationModelChange={handlePaginationModelChange}
      paginationModel={pagination}
      sortingMode="server"
      onSortModelChange={handlesortModelChange}
      sortModel={sort}
      columns={columns}
      getRowId={handleGetRowId}
      onColumnHeaderClick={handleColumnHeaderClick}
      onCellClick={handleCellClick}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      style={{ zIndex: 0 }}
      pageSizeOptions={[5, 10, 20, 50, 100]}
      onRowDoubleClick={handleRowDoubleClick}
      // checkboxSelection
      {...rest}
    />
  );
};
